import { computed, ref } from 'vue'
import { api } from '@/global/services/api'
import { isArray, isFunction } from '@/global/utils/utils'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import { TASK_CODEBOOK_TYPES } from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'

/**
 * The codebooks the task screens pick from - the task type, its category, the surface,
 * the cleaning type, the spreading material and how a street was drawn.
 *
 * They are a fixed, small set that never changes while the app is open, so they are
 * fetched once for the whole module rather than per screen: the listing filters, the
 * basic information tab, the create screen and the street dialog all read the very
 * same refs.
 */

const codebooks = ref({})
let requested = false

function fetchCodebooks () {
  if (requested) {
    return
  }
  requested = true

  api(WINTER_SERVICE_MODULE).get('codebook', { types: TASK_CODEBOOK_TYPES })
    .then(({ data }) => {
      codebooks.value = data || {}
    })
    .catch((exception) => {
      console.error(exception)
      // Left un-requested so the next screen that needs them tries again.
      requested = false
    })
}

export function useTaskCodebooks (options) {
  const { translate } = options || {}

  const label = (value) => isFunction(translate) ? translate(value) : value

  fetchCodebooks()

  /**
   * The entries of one codebook type as the autocompletes want them: `{ id, name }`,
   * the name being the human label. The backend keeps that label as a translation key
   * on `description` and this endpoint - unlike the task endpoints - hands it over
   * untranslated, so it is resolved here.
   */
  const itemsOf = (typeName) => {
    const entries = codebooks.value?.[typeName.toLowerCase()]

    if (!isArray(entries)) {
      return []
    }

    return entries.map(entry => ({
      id: Number(entry.id),
      name: entry.description ? label(entry.description) : entry.name,
      // Kept so a caller can tell a polygon from a polyline without another lookup.
      code: entry.name
    }))
  }

  const taskTypeItems = computed(() => itemsOf('WS_TASK_TYPE'))
  const taskCategoryItems = computed(() => itemsOf('WS_TASK_CATEGORY'))
  const surfaceTypeItems = computed(() => itemsOf('ROADWAY_SIDEWALK_TYPE'))
  const cleaningTypeItems = computed(() => itemsOf('WS_CLEANING_TYPE'))
  const spreadTypeItems = computed(() => itemsOf('WS_SPREAD_TYPE'))
  const pathTypeItems = computed(() => itemsOf('WS_PATH_TYPE'))

  /**
   * The id of one path type by its codebook name - what the street dialog sends after
   * the user drew a polyline or a polygon on the map.
   */
  const pathTypeIdByCode = (code) => pathTypeItems.value.find(item => item.code === code)?.id ?? null

  /**
   * The codebook name behind a path type id - how the street form tells whether it is
   * editing a polygon, which has no length, width, passes or road type.
   */
  const pathTypeCodeById = (id) => pathTypeItems.value.find(item => Number(item.id) === Number(id))?.code ?? null

  return {
    taskTypeItems,
    taskCategoryItems,
    surfaceTypeItems,
    cleaningTypeItems,
    spreadTypeItems,
    pathTypeItems,
    pathTypeIdByCode,
    pathTypeCodeById
  }
}
